import './FrozenColumnsTable.scss';
import cn from 'classnames';
import {
  Dispatch,
  JSX,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useContainerSize } from '../../utils/hooks/useContainerSize';
import { useScrollbarSizes } from '../../utils/hooks/useDOMHelpers';
import { Header } from '../StickyTable/BPMTable';
import { DraggableRow } from '../StickyTable/DraggableRow';
import {
  CellRenderer,
  CornerLocation,
  StickyTable,
  SuperTopItemType
} from '../StickyTable/StickyTable';

const MIN_COLUMN_WIDTH = 100;

interface RightCell {
  originalRowIndex: number;
  content: unknown;
}

/*
    Component that utilizes StickyTable to create a table with frozen left columns and
    scrollable right columns. Does not include a filter bar — add one in the parent component.
    Sorting is only available for right columns; left-column sorting must be driven by
    the parent via setRightData. See Segmentation Labeling Tool for an example.
*/
interface FrozenColumnsTableProps {
  leftWidth: number;
  leftRenderer: CellRenderer<Record<string, unknown>>;
  cornerRenderer: (location: CornerLocation) => JSX.Element;
  rightTableHeaders: Header[];
  rightTableHeaderNames: string[];
  originalRightData: Record<string, unknown>[];
  leftDataFields: string[];
  rightData: RightCell[][];
  setRightData: Dispatch<SetStateAction<RightCell[][]>>;
  noRowsRenderer: () => JSX.Element;
  moveRow?: (from: number, to: number) => void;
  onDragEnd?: () => void;
  isDraggable?: boolean;
  fillContainer?: boolean;
  topHeight?: number;
  superTopData?: SuperTopItemType<unknown>[];
  superTopRenderer?: CellRenderer<unknown>;
  superTopHeight?: number;
  leftSuperTopRenderer?: () => JSX.Element;
}

export const FrozenColumnsTable = ({
  leftWidth,
  leftRenderer,
  cornerRenderer,
  rightTableHeaders,
  rightTableHeaderNames,
  originalRightData,
  leftDataFields,
  rightData,
  setRightData,
  noRowsRenderer,
  moveRow = () => {},
  onDragEnd = () => {},
  isDraggable = false,
  fillContainer = false,
  topHeight,
  superTopData,
  superTopRenderer,
  superTopHeight,
  leftSuperTopRenderer
}: FrozenColumnsTableProps) => {
  const outerRef = useRef<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const scrollbarSizes = useScrollbarSizes(containerRef);
  // Measure the outer div (always mounted) rather than the inner
  // `.sticky-table-container` (only mounted once `hasData` is true) — a ref on
  // a conditional element wouldn't be attached when useContainerSize's effect
  // first runs, and the effect wouldn't re-run when it later mounts.
  const containerSize = useContainerSize(outerRef);

  // Reuse the previous array when rows are element-wise unchanged, so a new
  // `originalRightData` ref holding the same rows (parents re-derive it on
  // unrelated state changes) doesn't re-run the `leftData` rebuild or the effect
  // below. Storing during render is React's ref-free way to cache across renders.
  const [stableRightData, setStableRightData] = useState(originalRightData);
  if (
    stableRightData !== originalRightData &&
    (stableRightData.length !== originalRightData.length ||
      stableRightData.some((row, i) => row !== originalRightData[i]))
  ) {
    setStableRightData(originalRightData);
  }

  const topData = useMemo(() => rightTableHeaders.map(h => h.label ?? ''), [rightTableHeaders]);

  const cellRenderer = useCallback(
    ({ data, style, classes, columnIndex }) => {
      const headerClassName = rightTableHeaders[columnIndex]?.className;
      if (!data) return <div style={style} className={cn(classes, headerClassName)} />;

      const cellClasses = [...classes];
      const { originalRowIndex } = data;
      const { renderer } = rightTableHeaders[columnIndex];
      const rendered = renderer ? renderer(stableRightData[originalRowIndex]) : null;

      const cell = (
        <div className={cn(cellClasses, headerClassName)} style={style}>
          {rendered instanceof Date ? rendered.toLocaleDateString() : rendered}
        </div>
      );

      if (isDraggable && moveRow) {
        return (
          <DraggableRow
            index={originalRowIndex}
            moveRow={moveRow}
            onDragEnd={onDragEnd}
            isDragSource={columnIndex === 0}
          >
            {cell}
          </DraggableRow>
        );
      }

      return cell;
    },
    [rightTableHeaders, stableRightData, isDraggable, moveRow, onDragEnd]
  );

  const columnWidth = useCallback(
    (tableWidth: number, i: number): number => {
      let nonFlexWidth = scrollbarSizes.width;
      let flexSum = 0;

      const headerSizes: {
        flex?: number;
        minFlexWidth?: number;
        width?: number;
      }[] = [];

      for (const { width, minFlexWidth, flex: flexGeneric } of rightTableHeaders) {
        let flex: number | undefined;
        if (typeof flexGeneric === 'boolean') {
          flex = flexGeneric ? 1 : 0;
        } else {
          flex = flexGeneric;
        }

        const ourWidth = width ?? MIN_COLUMN_WIDTH;
        if (flex) {
          flexSum += flex;
        } else {
          nonFlexWidth += width ?? MIN_COLUMN_WIDTH;
        }
        headerSizes.push({ flex, width: ourWidth, minFlexWidth });
      }

      let flexElementTooSmall: boolean;
      let flexScraps: number;
      do {
        flexElementTooSmall = false;
        flexScraps = 0;
        const perFlexUnitWidthRaw = (tableWidth - nonFlexWidth) / flexSum;
        const perFlexUnitWidth = Math.floor(perFlexUnitWidthRaw);
        const perFlexUnitScrap = perFlexUnitWidthRaw - perFlexUnitWidth;
        for (let j = 0; j < headerSizes.length; ++j) {
          const { flex, minFlexWidth } = headerSizes[j];
          if (flex) {
            const actualMin = Math.max(minFlexWidth || 0, MIN_COLUMN_WIDTH);
            const ourWidth = perFlexUnitWidth * flex;
            if (ourWidth < actualMin) {
              flexElementTooSmall = true;
              headerSizes[j] = { width: actualMin };
              nonFlexWidth += actualMin;
              flexSum -= flex;
            } else {
              flexScraps += perFlexUnitScrap * flex;
              headerSizes[j] = { width: ourWidth, minFlexWidth, flex };
            }
          }
        }
      } while (flexElementTooSmall);

      const headerWidths = headerSizes.map(h => h.width ?? 0);
      const scrapDistribution = Math.floor(flexScraps / headerWidths.length);
      let scrapBonuses = flexScraps % headerWidths.length;
      for (let j = 0; j < headerSizes.length; ++j) {
        headerWidths[j] += scrapDistribution;
        if (scrapBonuses > 1) {
          headerWidths[j] += 1;
          scrapBonuses--;
        }
      }

      return headerWidths[i];
    },
    [rightTableHeaders, scrollbarSizes.width]
  );

  // Only rebuild rightData when the set of rows or column names actually changes.
  // `stableRightData` already collapses ref-only changes (same row objects in a
  // new array) to a stable ref, so rows can be compared by ref here. Column names
  // still need an element-wise compare since the parent re-derives that array too.
  const prevHeaderNamesRef = useRef<string[] | null>(null);
  const prevStableDataRef = useRef<Record<string, unknown>[] | null>(null);

  useEffect(() => {
    const sameRows = prevStableDataRef.current === stableRightData;
    const prevHeaders = prevHeaderNamesRef.current;
    const sameHeaders =
      prevHeaders !== null &&
      prevHeaders.length === rightTableHeaderNames.length &&
      prevHeaders.every((name, i) => name === rightTableHeaderNames[i]);

    prevStableDataRef.current = stableRightData;
    prevHeaderNamesRef.current = rightTableHeaderNames;

    if (sameRows && sameHeaders) return;

    const data: RightCell[][] = [];
    for (let i = 0; i < stableRightData.length; ++i) {
      const obj = stableRightData[i];
      const row: RightCell[] = [];
      for (const name of rightTableHeaderNames) {
        const content = obj[name ?? 'undefined'];
        row.push({
          originalRowIndex: i,
          content: content ?? ''
        });
      }
      data.push(row);
    }
    setRightData(data);
  }, [stableRightData, setRightData, rightTableHeaderNames]);

  const leftData = useMemo(() => {
    if (rightData.length !== stableRightData.length) return undefined;
    if (rightData.some(arr => arr.length === 0)) return undefined;

    return rightData.map(rightRow => {
      const row = stableRightData[rightRow[0].originalRowIndex];
      const leftRow: Record<string, unknown> = {};
      for (const field of leftDataFields) {
        leftRow[field] = row[field];
      }
      return leftRow;
    });
  }, [rightData, stableRightData, leftDataFields]);

  const topRenderer = useCallback(
    ({ data, style, classes, columnIndex }) => (
      <div style={style} className={cn(classes, rightTableHeaders[columnIndex]?.className)}>
        <span>{data}</span>
      </div>
    ),
    [rightTableHeaders]
  );

  const hasData = rightData.length && leftData?.length && topData.length;

  const showLeftSuperTop = Boolean(
    leftSuperTopRenderer && superTopHeight && hasData && superTopData?.length
  );

  return (
    <div ref={outerRef} className='frozen-columns-table'>
      {hasData ? (
        <div ref={containerRef} className='sticky-table-container'>
          {containerSize && containerSize.width > 0 && containerSize.height > 0 && (
            <StickyTable
              data={rightData}
              width={containerSize.width}
              height={containerSize.height}
              cellRenderer={cellRenderer}
              columnWidth={idx => columnWidth(containerSize.width - leftWidth, idx)}
              topData={topData}
              topRenderer={topRenderer}
              leftWidth={leftWidth}
              leftData={leftData}
              leftRenderer={leftRenderer}
              cornerRenderer={cornerRenderer}
              fillContainer={fillContainer}
              topHeight={topHeight}
              superTopData={superTopData}
              superTopRenderer={superTopRenderer}
              superTopHeight={superTopHeight}
            />
          )}
          {showLeftSuperTop && leftSuperTopRenderer && (
            <div
              className='left-super-top'
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: leftWidth,
                height: superTopHeight,
                zIndex: 2
              }}
            >
              {leftSuperTopRenderer()}
            </div>
          )}
        </div>
      ) : (
        noRowsRenderer()
      )}
    </div>
  );
};
